import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
  agentPromptWarningConsequence,
  describeAgentPromptWarning,
} from '@sb/feathers-types';
import type {
  AgentFeedbackCreateInput,
  AgentFeedbackData,
} from '@sb/feathers-types';
import { toolDisplayName } from '@sb/remote-control/util/toolDisplayName';
import { useAgentPromptWarnings } from '@sbrc/components/agent/AgentPromptWarnings';
import { usePageRobotRoutine } from '@sbrc/contexts';
import { useFeatureFlag, useRouter } from '@sbrc/hooks';
import { onGetAgentSessions } from '@sbrc/services/feathers-client/agent-sessions';
import { getApp } from '@sbrc/services/feathers-client/utils';

import { parseConfirmation } from './chatEventHandlers';
import type { ChatHeaderMenuItem } from './ChatHeader';
import {
  toChatHistorySession,
  type ChatHistorySession,
} from './ChatHistorySession';
import { ChatHistoryView } from './ChatHistoryView';
import { ChatInput } from './ChatInput';
import { CHAT_MINIMIZED_NEW_CHAT_TIMEOUT_MS } from './chatPanelConstants';
import { ChatPanelOverlay } from './ChatPanelOverlay';
import {
  ChatPanelProvider,
  useChatPanel,
  type PendingConfirmation,
} from './ChatPanelProvider';
import { type ChatTransport, createRealChatTransport } from './ChatTransport';
import type { ChatWarning } from './ChatWarnings';
import {
  ConfirmationModal,
  type ConfirmationDecision,
} from './ConfirmationModal';
import { DeleteSessionModal } from './DeleteSessionModal';
import {
  LandingHero,
  type LandingOption,
  landingOptionLabel,
} from './LandingHero';
import { MessageFeedback, type FeedbackPayload } from './MessageFeedback';
import { MessageList } from './MessageList';
import type { ChatMessage } from './types';
import { useChatHistory, type ChatHistoryOperations } from './useChatHistory';
import { useChatMessages, type ChatStreamSnapshot } from './useChatMessages';
import { useMessageTaskTimings } from './useMessageTaskTimings';
import { useNavigateEventHandler } from './useNavigateEventHandler';
import { usePoseSimulationWarning } from './usePoseSimulationWarning';
import { useResolveConfirmation } from './useResolveConfirmation';
import { useServicesRestartGate } from './useServicesRestartGate';
import {
  useSessionFeedback,
  type SessionFeedbackOperations,
} from './useSessionFeedback';

interface RoutineAgentChatBodyProps {
  transport: ChatTransport;
}

/**
 * Inner body of the Routine Agent Chat mount. Pulled out so it can
 * read `useChatPanel()` (which throws when used outside the
 * provider) and `usePageRobotRoutine()` (which mirrors the routine
 * currently open in the editor).
 *
 * Backend interaction is routed through the injected `transport`;
 * the events subscription, message state, and send pipeline live
 * in `useChatMessages` so this component only handles layout +
 * slot wiring.
 *
 * Mounting lives at the router-root layout so a React Router push
 * doesn't unmount the panel — the chat survives navigation between
 * pages and the conversation state doesn't reset.
 */
export function RoutineAgentChatBody({ transport }: RoutineAgentChatBodyProps) {
  const { robot, routine } = usePageRobotRoutine();
  const { query } = useRouter();
  const isTaskTimingEnabled = useFeatureFlag('routineAgentTaskTiming');

  // Editing routine = URL-derived only (no fallback). Distinguishes
  // "the routine the user has open in the editor" from
  // `routine?.id`, which falls back to `robot.latestRoutineID` off
  // `/routines/:id`. The agent uses this to resolve "this routine"
  // unambiguously against the page the operator is on.
  const urlRoutineID = Array.isArray(query.routineID)
    ? query.routineID[0]
    : query.routineID;

  const editingRoutineID =
    typeof urlRoutineID === 'string' && urlRoutineID.length > 0
      ? urlRoutineID
      : null;

  // Loaded routine = `robot.latestRoutineID`. The routine the robot
  // will run on Play, regardless of which routine the editor is
  // showing. Lets the agent resolve "what's loaded" / "the running
  // routine" against the right id even when editing diverges.
  const loadedRoutineID = robot?.latestRoutineID ?? null;

  const {
    isOpen,
    close,
    currentSessionID,
    setCurrentSessionID,
    setCurrentRoutineID,
    pendingConfirmation,
    setPendingConfirmation,
    chatView,
    setChatView,
    startNewChat,
  } = useChatPanel();

  // Composer draft, owned here rather than inside ChatInput: the
  // overlay unmounts its footer on minimize, so component-local text
  // would be lost on every close/reopen.
  const [inputDraft, setInputDraft] = useState('');

  const isRestartingServices = useServicesRestartGate();

  // Standing configuration problems, surfaced off the header's warning icon.
  // Read only while the panel is open: nothing can act on them when it is
  // closed, and the icon they drive is part of the panel.
  const poseSimulationWarning = usePoseSimulationWarning(isOpen);
  const promptWarnings = useAgentPromptWarnings(isOpen);

  const warnings = useMemo<ChatWarning[]>(
    () => [
      ...(poseSimulationWarning ? [poseSimulationWarning] : []),
      ...promptWarnings.map((warning) => ({
        // One warning per template and kind, which is what makes the pair
        // unique and stable across reads.
        id: `${warning.template}:${warning.kind}`,
        message: `${describeAgentPromptWarning(warning)} ${agentPromptWarningConsequence(
          warning,
        )} Update it in Control Panel → Agent Settings.`,
      })),
    ],
    [poseSimulationWarning, promptWarnings],
  );

  // Clear the draft when the bound session genuinely changes (Chat
  // History load, "New chat") so session A's half-typed text never
  // pre-fills session B's composer. Mirrors useChatMessages' reset
  // semantics: the null → realID first-bind is NOT a switch (that's
  // the BE allocating an id for the message just sent — wiping then
  // could eat text typed during the first turn).
  const draftSessionRef = useRef<string | null>(currentSessionID);

  useEffect(() => {
    const previous = draftSessionRef.current;

    draftSessionRef.current = currentSessionID;

    if (previous !== null && currentSessionID !== previous) {
      setInputDraft('');
    }
  }, [currentSessionID]);

  // Dispatch backend-emitted `navigate` events into react-router.
  // The handler is stable-by-ref so it doesn't churn the events
  // subscription. Pulling `useNavigate` from the router context
  // works here because the mount lives below `<RootLayout />`,
  // which is itself a child of `<RouterProvider>`.
  const navigate = useNavigate();
  const dispatchNavigate = useNavigateEventHandler({ navigate });

  // Monotonic counter for navigation-card React keys. Deriving the key from
  // url+label collides when the agent offers the same destination twice in a
  // turn; a per-mount sequence guarantees a unique, stable key per card.
  const navCardSeqRef = useRef(0);

  // Routes /events frames the dedicated handlers don't recognize.
  // Navigate frames flip the router; confirmation_request frames
  // open the tool-use approval modal via the provider. Approve / Reject
  // POSTs /confirm through `useResolveConfirmation` (wired below); on
  // failure the modal stays open so the user can retry.
  const handleUnknownEvent = useCallback(
    (event: unknown, append: (message: ChatMessage) => void) => {
      if (dispatchNavigate(event)) return;

      if (typeof event !== 'object' || event === null) return;

      const { type } = event as { type?: unknown };

      // `card` frames render an inline card in the transcript. Today the only
      // server-emitted kind is `navigation` (a clickable affordance that routes
      // the SPA behind the still-open chat). Any other kind — or a malformed
      // navigation payload — is dropped with a warning, for diagnostic parity
      // with the agentMessages seeding loop below.
      if (type === 'card') {
        const { cardKind, payload } = event as {
          cardKind?: unknown;
          payload?: unknown;
        };

        if (
          cardKind === 'navigation' &&
          typeof payload === 'object' &&
          payload !== null
        ) {
          const { url, label } = payload as { url?: unknown; label?: unknown };

          if (typeof url === 'string' && typeof label === 'string') {
            navCardSeqRef.current += 1;

            append({
              id: `nav-card-${navCardSeqRef.current}`,
              role: 'assistant',
              text: '',
              card: { kind: 'navigation', url, label },
            });

            return;
          }
        }

        // eslint-disable-next-line no-console -- surface dropped card frames
        console.warn(
          'RoutineAgentChatMount: dropping unsupported or malformed card frame',
          event,
        );

        return;
      }

      if (type !== 'confirmation_request') return;

      const confirmation = parseConfirmation(event);

      if (confirmation !== null) {
        setPendingConfirmation(confirmation satisfies PendingConfirmation);
      }
    },
    [dispatchNavigate, setPendingConfirmation],
  );

  // The (re)connect snapshot carries any still-pending tool-use
  // confirmation — and the events endpoint deliberately filters that
  // same request out of its replay, so the snapshot is the ONLY
  // delivery on a fresh subscription. Restore it into the approval
  // modal; without this a page reload (or stream reconnect after the
  // provider state was lost) leaves the turn silently blocked on the
  // gate until it times out. The snapshot is authoritative in both
  // directions: null means the gate was resolved while we were
  // disconnected (another tab, or a server-side timeout), so any modal
  // still open for it must dismiss — Approve/Reject would POST a
  // toolUseID the orchestrator no longer tracks.
  const handleSnapshot = useCallback(
    (snapshot: ChatStreamSnapshot) => {
      setPendingConfirmation(snapshot.pendingConfirmation);
    },
    [setPendingConfirmation],
  );

  // Bumped when the events stream detects it lost frames for good (seq
  // gap across a reconnect, or a turn that ended while disconnected).
  // Re-runs the seed effect below so `messages` rebuilds from the
  // persisted feathers history — the only complete source at that
  // point. Fired at turn end, and the application itself goes through
  // `applySeed`, which refuses to overwrite a turn that started while
  // the seed fetch was in flight.
  const [seedNonce, setSeedNonce] = useState(0);

  const handleResyncNeeded = useCallback(() => {
    setSeedNonce((n) => n + 1);
  }, []);

  const {
    messages,
    isSending,
    streamError,
    agentError,
    isThinking,
    isTurnActive,
    isCancelling,
    sendMessage,
    stopTurn,
    retryMessage,
    reset,
    applySeed,
  } = useChatMessages({
    transport,
    currentSessionID,
    editingRoutineID,
    loadedRoutineID,
    onUnknownEvent: handleUnknownEvent,
    onSnapshot: handleSnapshot,
    // [TFIX] Pin the BE-issued sessionID into the provider on the
    // first /send of a brand-new chat. Without this, follow-up sends
    // would all POST sessionID: null and the /events subscription
    // would never open.
    onSessionCreated: setCurrentSessionID,
    onResyncNeeded: handleResyncNeeded,
  });

  // POST the user's approve/reject to /confirm so the orchestrator's
  // ConfirmationGate resolves and the gated tool actually dispatches.
  // Clearing pendingConfirmation alone (the old behaviour) closed the
  // modal but left the agent's turn blocked on the gate until timeout —
  // which is why confirmation-gated tools (configure_equipment,
  // delete_routine, …) never ran after the user clicked OK.
  const { resolve: resolveConfirmation, isResolving: isResolvingConfirmation } =
    useResolveConfirmation({
      postConfirm: async ({ sessionID, toolUseID, decision }) => {
        const res = await transport.confirm({
          sessionID,
          toolUseID,
          approved: decision === 'approve',
        });

        return { status: res.status };
      },
      onResolved: () => setPendingConfirmation(null),
      // On failure, keep the modal open: the orchestrator never received
      // the decision, so dismissing here would leave the turn blocked on
      // the confirmation gate with no UI left to approve or reject.
      // `isResolving` drops in the hook's finally, re-enabling the
      // buttons so the user can retry (or Escape to reject).
      onError: () => {},
    });

  const handleConfirmationResolve = useCallback(
    (decision: ConfirmationDecision) => {
      const pending = pendingConfirmation;

      // No pending confirmation, or no session to POST the decision
      // against (a sessionless confirmation is stale by definition) —
      // nothing can be resolved server-side, so just dismiss.
      if (pending === null || currentSessionID === null) {
        setPendingConfirmation(null);

        return;
      }

      resolveConfirmation({
        sessionID: currentSessionID,
        toolUseID: pending.toolUseID,
        decision,
      });
    },
    [
      pendingConfirmation,
      currentSessionID,
      resolveConfirmation,
      setPendingConfirmation,
    ],
  );

  useEffect(() => {
    setCurrentRoutineID(routine?.id ?? null);
  }, [routine?.id, setCurrentRoutineID]);

  // Memoized: feathersApp sits in the seed effect's deps, so its
  // identity must be render-stable — an accessor that returned a fresh
  // handle per render would re-run the seed on every render, and the
  // seed's own setMessages makes that a self-sustaining loop.
  const feathersApp = useMemo(() => getApp(), []);

  const chatHistoryOperations = useMemo<ChatHistoryOperations>(
    () => ({
      subscribe: ({ routineID: rid }, { onSessions, onError }) =>
        onGetAgentSessions({
          routineID: rid,
          onSnapshot: (rows) => onSessions(rows.map(toChatHistorySession)),
          onError,
        }),
      rename: async (sessionID, newTitle) => {
        // `manual` freezes the title against the agent's own titling,
        // which otherwise renames a chat after the routine it writes.
        const row = await feathersApp
          .service('agentSessions')
          .patch(sessionID, { title: newTitle, titleSource: 'manual' });

        return toChatHistorySession(row);
      },
      remove: async (sessionID) => {
        await feathersApp
          .service('agentSessions')
          .patch(sessionID, { deleted: true });
      },
    }),
    [feathersApp],
  );

  // Last rename/delete failure, surfaced in the history view.
  const [historyOperationError, setHistoryOperationError] =
    useState<Error | null>(null);

  const handleHistoryError = useCallback((err: Error) => {
    // eslint-disable-next-line no-console -- surface history op failures
    console.warn('RoutineAgentChatMount: chat-history operation failed', err);

    setHistoryOperationError(err);
  }, []);

  const {
    sessions: historySessions,
    status: historyStatus,
    error: historyError,
    reload: reloadHistory,
    renameSession: renameHistorySession,
    deleteSession: deleteHistorySession,
  } = useChatHistory({
    routineID: null, // global view — all sessions
    operations: chatHistoryOperations,
    onError: handleHistoryError,
  });

  // Thumbs verdicts are persisted as `agentFeedback` rows, but the
  // thumbs themselves only knew about verdicts submitted in the current
  // mount — so re-opening a chat showed every message as un-rated.
  // Load the session's rows and hand them to `MessageFeedback`.
  const sessionFeedbackOperations = useMemo<SessionFeedbackOperations>(
    () => ({
      list: async (sessionID) => {
        const result = await feathersApp.service('agentFeedback').find({
          // One row per rated message (UNIQUE on messageID), so the
          // paginate max comfortably covers any real session.
          query: { sessionID, $limit: 500 },
        });

        // `Service.find` is typed as `T | T[] | Paginated<T>`; the
        // service paginates, but accept the bare shapes too.
        let rows: AgentFeedbackData[] = [];

        if (Array.isArray(result)) {
          rows = result;
        } else if ('data' in result) {
          rows = result.data;
        }

        return rows.map((row) => ({
          messageID: row.messageID,
          sentiment:
            row.sentiment === 'up'
              ? ('positive' as const)
              : ('negative' as const),
          // The column is nullable; the thumbs row treats an absent
          // reason as "no quote to render".
          reason: row.content ?? undefined,
          // Rows from before the tag row shipped have no `tags` column
          // value in older clients' caches; treat that as none.
          tags: row.tags ?? [],
          quote: row.quote ?? undefined,
        }));
      },
    }),
    [feathersApp],
  );

  const timingForMessage = useMessageTaskTimings(
    currentSessionID,
    messages,
    isTaskTimingEnabled,
  );

  const { feedbackByMessageID, recordSubmission: recordFeedbackSubmission } =
    useSessionFeedback({
      sessionID: currentSessionID,
      operations: sessionFeedbackOperations,
      onError: (err) => {
        // Best-effort, exactly like the create: a chat that can't show
        // past verdicts is far better than one that fails to render.
        // eslint-disable-next-line no-console
        console.warn(
          'RoutineAgentChatMount: failed to load agentFeedback history',
          err,
        );
      },
    });

  // Keep copy of session during deletion processing to prevent flickering
  const [pendingDeleteSession, setPendingDeleteSession] =
    useState<ChatHistorySession | null>(null);

  const [isDeletingSession, setIsDeletingSession] = useState(false);

  const cancelDeleteSession = useCallback(() => {
    setPendingDeleteSession(null);
  }, []);

  const confirmDeleteSession = useCallback(async () => {
    if (pendingDeleteSession === null) return;

    const sessionID = pendingDeleteSession.id;

    setIsDeletingSession(true);
    setHistoryOperationError(null);

    let deleted = false;

    try {
      // `deleteSession` rolls back and resolves `false` rather than
      // rejecting, so the result is the only success signal.
      deleted = await deleteHistorySession(sessionID);
    } finally {
      setIsDeletingSession(false);
      setPendingDeleteSession(null);
    }

    if (!deleted) return;

    if (sessionID === currentSessionID) {
      setCurrentSessionID(null);
      reset();
    }
  }, [
    currentSessionID,
    deleteHistorySession,
    pendingDeleteSession,
    reset,
    setCurrentSessionID,
  ]);

  // [TFIX] Seed message history from feathers `agentMessages` whenever
  // currentSessionID transitions to a non-null value (either via a
  // routine-binding hook OR via clicking a row in the Chat history
  // view). The /events stream still owns deltas for live turns; this
  // seed populates the past.
  useEffect(() => {
    if (currentSessionID === null) return undefined;

    let canceled = false;
    const sessionID = currentSessionID;

    (async () => {
      try {
        const result = await (feathersApp.service as any)('agentMessages').find(
          { query: { sessionID, $sort: { sequence: 1 } } },
        );

        const rows: unknown[] = Array.isArray(result)
          ? result
          : result?.data ?? [];

        if (canceled) return;

        const seeded: ChatMessage[] = [];
        // Index of the in-flight tool group bubble (or -1 if none).
        // A persisted assistant turn may end with one or more
        // tool_use blocks; the matching tool_result blocks live on
        // the next `tool` row and we append into the same bubble.
        let currentToolGroupIdx = -1;

        for (const row of rows) {
          if (typeof row !== 'object' || row === null) continue;

          const candidate = row as {
            id?: unknown;
            role?: unknown;
            content?: unknown;
          };

          if (
            typeof candidate.id !== 'string' ||
            !Array.isArray(candidate.content)
          ) {
            // eslint-disable-next-line no-console
            console.warn(
              'RoutineAgentChatMount: skipping malformed agentMessages row',
              row,
            );

            continue;
          }

          if (candidate.role === 'user' || candidate.role === 'assistant') {
            const { role } = candidate;
            let textForBubble = '';

            // Walk content blocks in order. Text blocks accumulate
            // into one bubble; each tool_use block starts (or
            // appends to) a per-turn tool group below it.
            for (const rawBlock of candidate.content) {
              if (typeof rawBlock !== 'object' || rawBlock === null) continue;

              const block = rawBlock as {
                type?: unknown;
                text?: unknown;
                id?: unknown;
                name?: unknown;
              };

              if (block.type === 'text' && typeof block.text === 'string') {
                textForBubble += block.text;

                continue;
              }

              if (
                block.type === 'tool_use' &&
                typeof block.id === 'string' &&
                typeof block.name === 'string'
              ) {
                // Flush any pending text into its own bubble first
                // so the tool card renders after the text.
                if (textForBubble.length > 0) {
                  seeded.push({
                    id: `${candidate.id}-text`,
                    messageID: candidate.id,
                    role,
                    text: textForBubble,
                  });

                  textForBubble = '';
                }

                const item = {
                  id: block.id,
                  label: toolDisplayName(block.name),
                  status: 'running' as const,
                };

                if (currentToolGroupIdx < 0) {
                  seeded.push({
                    id: `${candidate.id}-tools`,
                    messageID: candidate.id,
                    role: 'assistant',
                    text: '',
                    card: {
                      kind: 'toolGroup',
                      tools: [item],
                      controlled: true,
                    },
                  });

                  currentToolGroupIdx = seeded.length - 1;
                } else {
                  const target = seeded[currentToolGroupIdx];

                  if (target.card?.kind === 'toolGroup') {
                    seeded[currentToolGroupIdx] = {
                      ...target,
                      card: {
                        ...target.card,
                        tools: [...target.card.tools, item],
                      },
                    };
                  }
                }
              }
            }

            if (textForBubble.length > 0) {
              seeded.push({
                id: candidate.id,
                messageID: candidate.id,
                role,
                text: textForBubble,
              });
            }

            continue;
          }

          if (candidate.role === 'tool') {
            // Patch tool_result blocks into the matching tool item
            // of the most recent open tool group.
            if (currentToolGroupIdx < 0) continue;

            const target = seeded[currentToolGroupIdx];

            if (target.card?.kind !== 'toolGroup') continue;

            let { tools } = target.card;

            for (const rawBlock of candidate.content) {
              if (typeof rawBlock !== 'object' || rawBlock === null) continue;

              const block = rawBlock as {
                type?: unknown;
                toolUseId?: unknown;
                tool_use_id?: unknown;
                isError?: unknown;
                is_error?: unknown;
              };

              // BE persists tool_result blocks with camelCase
              // `toolUseId` (lowercase 'd'). Accept snake_case too
              // for forward-compat with any future migration.
              let useID: string | null = null;

              if (typeof block.toolUseId === 'string') {
                useID = block.toolUseId;
              } else if (typeof block.tool_use_id === 'string') {
                useID = block.tool_use_id;
              }

              if (block.type !== 'tool_result' || useID === null) {
                continue;
              }

              const errorFlag =
                block.isError === true || block.is_error === true;

              const nextStatus = errorFlag
                ? ('error' as const)
                : ('complete' as const);

              tools = tools.map((t) =>
                t.id === useID ? { ...t, status: nextStatus } : t,
              );
            }

            seeded[currentToolGroupIdx] = {
              ...target,
              card: { ...target.card, tools },
            };

            // After a tool row we expect the next assistant turn
            // to start a fresh group, mirroring live-event behavior.
            currentToolGroupIdx = -1;
          }
        }

        if (canceled) return;

        // Turn-safe application: refused (returns false) if a turn
        // started while the find() above was in flight — the hook
        // re-arms its resync flag so the next turn end reseeds.
        applySeed(seeded);
      } catch (err) {
        if (canceled) return;

        // eslint-disable-next-line no-console
        console.warn(
          'RoutineAgentChatMount: failed to load agentMessages history',
          err,
        );
      }
    })();

    return () => {
      canceled = true;
    };
    // `seedNonce` re-runs the seed after a stream resync — same load,
    // fresher rows.
  }, [currentSessionID, feathersApp, applySeed, seedNonce]);

  // The services-restart gate is applied to the hero itself (`disabled`
  // below), not here: a pick permanently locks the picker, so swallowing
  // the callback would strand it with a selection that never sent.
  const handleLandingSelect = useCallback(
    (option: LandingOption) => {
      sendMessage(landingOptionLabel(option)).catch(() => {
        // sendMessage flips the user message to sendStatus: 'failed'
        // on failure so the inline retry affordance surfaces it; no
        // banner needed here.
      });
    },
    [sendMessage],
  );

  // The inline retry on a failed user message starts a full turn just
  // like the composer, so it honours the services-restart gate too. The
  // message keeps its failed state and the retry stays available once
  // the restart finishes.
  const handleRetry = useCallback(
    (messageID: string) => {
      if (isRestartingServices) return;

      retryMessage(messageID).catch(() => {
        // Same contract as sendMessage: a failed retry re-flags the
        // message as failed, so the affordance stays put.
      });
    },
    [retryMessage, isRestartingServices],
  );

  // Persist thumbs feedback as an `agentFeedback` row. The backend forwards
  // it to Braintrust (keyed on the rated message's turn). The UI sentiment
  // (`positive`/`negative`) maps to the schema's `up`/`down`. Best-effort:
  // a failed create must not disrupt the chat — the rejection is logged and
  // handed back to the thumbs row, which un-submits itself.
  const handleFeedbackSubmit = useCallback(
    (payload: FeedbackPayload) => {
      // Snapshot the durable ids of the messages currently in view so the
      // feedback row records the conversation context it was given against.
      const conversationSnapshotMessageIDs = Array.from(
        new Set(
          messages
            .map((m) => m.messageID)
            .filter((id): id is string => id !== undefined),
        ),
      );

      // No routineSnapshot here: feathers-bot's create hook freezes it
      // server-side from the session's own routine binding, so every
      // client gets the invariant without page-context races.
      const input: AgentFeedbackCreateInput = {
        sessionID: payload.sessionID,
        messageID: payload.messageID,
        sentiment: payload.sentiment === 'positive' ? 'up' : 'down',
        content: payload.content ?? null,
        tags: payload.tags,
        quote: payload.quote ?? null,
        conversationSnapshotMessageIDs,
      };

      return feathersApp
        .service('agentFeedback')
        .create(input)
        .then(() => {
          // Recorded only once the row exists. Holding it locally before
          // the write lands would leave a verdict postgres doesn't have
          // masking the real row for the rest of the session whenever the
          // create fails — notably the 400 the UNIQUE-on-messageID
          // constraint raises. From here it survives a re-seed of the
          // message list (which remounts every MessageFeedback).
          recordFeedbackSubmission(payload.messageID, {
            sentiment: payload.sentiment,
            reason: payload.content,
            tags: payload.tags,
            quote: payload.quote,
          });
        })
        .catch((err: unknown) => {
          // Best-effort: feedback failures are non-fatal to the chat.
          // eslint-disable-next-line no-console
          console.warn(
            'RoutineAgentChatMount: failed to persist agentFeedback',
            err,
          );

          // Re-thrown so the thumbs row un-submits — it owns the visible
          // state, and leaving it coloured would claim a rating that was
          // never stored.
          throw err;
        });
    },
    [feathersApp, messages, recordFeedbackSubmission],
  );

  const isHistoryView = chatView === 'history';

  // Wrap the provider's `startNewChat` so the mount also clears the
  // message pipeline. The provider can only clear what it owns
  // (currentSessionID, pendingConfirmation, chatView); reset()
  // handles the rest.
  const handleStartNewChat = useCallback(() => {
    startNewChat();
    reset();
    // Explicit (not just via the session-transition effect): "New
    // chat" from a session-less landing view never transitions
    // currentSessionID, but the user still asked for a clean slate.
    setInputDraft('');
  }, [startNewChat, reset]);

  // A panel reopened after sitting minimized past
  // CHAT_MINIMIZED_NEW_CHAT_TIMEOUT_MS lands on a fresh chat instead
  // of resuming the stale session (still reachable via Chat history).
  // Layout effect, not effect: the unbind must commit before the
  // browser paints the reopened panel so the old conversation never
  // flashes. Timestamp comparison rather than a live timer so a
  // background tab's throttled timers can't delay the expiry.
  const minimizedAtRef = useRef<number | null>(null);
  const wasOpenRef = useRef(isOpen);

  useLayoutEffect(() => {
    const wasOpen = wasOpenRef.current;

    wasOpenRef.current = isOpen;

    // Stamp only on the open → closed transition; re-runs from other
    // dep changes while closed must not push the expiry later.
    if (wasOpen && !isOpen) {
      minimizedAtRef.current = Date.now();

      return;
    }

    if (wasOpen || !isOpen) return;

    const minimizedAt = minimizedAtRef.current;

    minimizedAtRef.current = null;

    if (minimizedAt === null) return;

    if (Date.now() - minimizedAt < CHAT_MINIMIZED_NEW_CHAT_TIMEOUT_MS) return;

    // An in-flight turn keeps the session alive: expiring here would
    // yank a running turn out of view, and a pending confirmation
    // would strand the orchestrator's gate with no UI to resolve it.
    if (isSending || isThinking || pendingConfirmation !== null) return;

    // Already on the landing hero — nothing to expire, and wiping
    // would needlessly eat a sessionless composer draft.
    if (currentSessionID === null && messages.length === 0) return;

    handleStartNewChat();
  }, [
    isOpen,
    isSending,
    isThinking,
    pendingConfirmation,
    currentSessionID,
    messages,
    handleStartNewChat,
  ]);

  const menuItems = useMemo<ChatHeaderMenuItem[] | undefined>(() => {
    if (isHistoryView) return undefined;

    return [
      {
        label: 'New chat',
        iconKind: 'plus',
        onClick: handleStartNewChat,
      },
      {
        label: 'Chat history',
        iconKind: 'clock',
        onClick: () => setChatView('history'),
      },
    ];
  }, [isHistoryView, handleStartNewChat, setChatView]);

  const leftAction = useMemo(() => {
    if (!isHistoryView) return undefined;

    return {
      iconKind: 'chevronLeft' as const,
      ariaLabel: 'Back to chat',
      onClick: () => setChatView('chat'),
    };
  }, [isHistoryView, setChatView]);

  const headerTitle = isHistoryView ? 'Chat history' : undefined;

  const renderFeedback = useCallback(
    (message: ChatMessage, index: number) => {
      if (currentSessionID === null) return null;
      if (message.role !== 'assistant') return null;
      if (message.isStreaming) return null;
      // Feedback targets the durable message id (not the per-bubble React
      // key); without it the backend can't resolve the rated message.
      if (message.messageID === undefined) return null;

      // History seeding fans one backend message out into several bubbles
      // (text-before-tools + tool group + trailing text) that share a
      // messageID. Render the thumbs only under the last text bubble for
      // that messageID so a single backend message can't collect multiple
      // agentFeedback rows.
      const lastTextBubbleForMessage = messages
        .filter(
          (m) =>
            m.messageID === message.messageID &&
            m.role === 'assistant' &&
            m.card === undefined,
        )
        .pop();

      if (lastTextBubbleForMessage?.id !== message.id) return null;

      return (
        <MessageFeedback
          sessionID={currentSessionID}
          messageID={message.messageID}
          index={index}
          onSubmit={handleFeedbackSubmit}
          submittedFeedback={feedbackByMessageID.get(message.messageID) ?? null}
          timing={timingForMessage(message.messageID)}
        />
      );
    },
    [
      currentSessionID,
      feedbackByMessageID,
      handleFeedbackSubmit,
      messages,
      timingForMessage,
    ],
  );

  return (
    <>
      <ChatPanelOverlay
        isOpen={isOpen}
        onClose={close}
        title={headerTitle}
        menuItems={menuItems}
        leftAction={leftAction}
        warnings={warnings}
        body={
          isHistoryView ? (
            <ChatHistoryView
              sessions={historySessions}
              activeSessionID={currentSessionID}
              onLoad={(sessionID) => {
                setCurrentSessionID(sessionID);
                setChatView('chat');
              }}
              onRename={(sessionID, newTitle) => {
                setHistoryOperationError(null);

                renameHistorySession(sessionID, newTitle).catch(() => {
                  // useChatHistory handles rollback + onError; swallow
                  // here so the inline handler doesn't blow up.
                });
              }}
              onRequestDelete={(sessionID) => {
                setPendingDeleteSession(
                  historySessions.find((s) => s.id === sessionID) ?? null,
                );
              }}
              // The subscription retries with backoff before giving
              // up, so reaching `error` means it is genuinely dead and
              // the list has stopped updating. Without this the panel
              // silently freezes and only a page reload recovers it —
              // the very failure this component exists to prevent.
              loadError={historyStatus === 'error' ? historyError : null}
              onRetryLoad={reloadHistory}
              operationError={
                historyStatus === 'error' ? null : historyOperationError
              }
            />
          ) : (
            <>
              {streamError ? (
                <div
                  role="status"
                  aria-live="polite"
                  className="tw-px-16 tw-py-8 tw-text-13 tw-text-red"
                >
                  Stream disconnected: {streamError.message}
                </div>
              ) : null}
              {/* Terminal agent error (token budget exhausted, LLM
                  unavailable, …). role="alert" because it's the end of the
                  turn and the operator must act (e.g. start a new chat),
                  unlike the transient disconnect status above. */}
              {agentError ? (
                <div
                  role="alert"
                  data-testid="chat-error-banner"
                  className="tw-px-16 tw-py-8 tw-text-13 tw-font-medium tw-text-red"
                >
                  {agentError.message}
                </div>
              ) : null}
              {messages.length === 0 ? (
                <LandingHero
                  onSelectOption={handleLandingSelect}
                  disabled={isRestartingServices}
                />
              ) : (
                <MessageList
                  messages={messages}
                  renderFeedback={renderFeedback}
                  onRetry={handleRetry}
                  onCardNavigate={navigate}
                  isThinking={isThinking}
                />
              )}
            </>
          )
        }
        footer={
          isHistoryView ? null : (
            <ChatInput
              onSubmit={sendMessage}
              // Stay editable while a turn is active: disabling drops focus,
              // and `isTurnActive` already suppresses submit. Only disable
              // when the turn ended but the send POST is still unsettled,
              // since `sendMessage` rejects those after the draft clears.
              disabled={isRestartingServices || (isSending && !isTurnActive)}
              placeholder={
                isRestartingServices ? 'Restarting services...' : undefined
              }
              // Controlled draft owned here (the mount outlives the
              // overlay) so a half-typed message survives minimize.
              value={inputDraft}
              onChange={setInputDraft}
              isTurnActive={isTurnActive}
              isStopDisabled={isCancelling}
              onStop={stopTurn}
            />
          )
        }
      />
      <ConfirmationModal
        pending={pendingConfirmation}
        onResolve={handleConfirmationResolve}
        isResolving={isResolvingConfirmation}
        panelIsOpen={isOpen}
      />
      <DeleteSessionModal
        session={pendingDeleteSession}
        onConfirm={confirmDeleteSession}
        onCancel={cancelDeleteSession}
        isDeleting={isDeletingSession}
      />
    </>
  );
}

interface RoutineAgentChatMountProps {
  children: React.ReactNode;
  /**
   * Backend adapter. Defaults to {@link createRealChatTransport},
   * which hits `/api/v1/agent/*` directly. Tests and the UI-only
   * dev harness pass an in-process mock that satisfies the same
   * {@link ChatTransport} contract.
   */
  transport?: ChatTransport;
  /**
   * Initial chat-panel state. Used by tests / dev harness to seed
   * `currentSessionID` without going through the routine session
   * binding hook. Production mounts leave this unset and let the
   * binding hook populate the provider.
   */
  initialState?: React.ComponentProps<typeof ChatPanelProvider>['initialState'];
}

/**
 * Routine Agent Chat mount point. Wraps its children (the layout
 * navbar + page content) in `ChatPanelProvider` so the navbar's
 * launcher button can open the panel via `useChatPanelOptional`,
 * then renders the overlay alongside them. Caller gates on both
 * `appRedesign` and `routineAgentChat` per the `app-redesign`
 * skill — see `PrivatePageLayoutInner.tsx`.
 */
export function RoutineAgentChatMount({
  children,
  transport,
  initialState,
}: RoutineAgentChatMountProps) {
  // Memoize the default transport so a re-render of the layout
  // doesn't tear down the events subscription on the body.
  const resolvedTransport = useMemo(
    () => transport ?? createRealChatTransport(),
    [transport],
  );

  return (
    <ChatPanelProvider initialState={initialState}>
      {children}
      <RoutineAgentChatBody transport={resolvedTransport} />
    </ChatPanelProvider>
  );
}
